/**
 * ExplodedScene.jsx — the 3D exploded assembly view for manifest-driven CAD.
 *
 * Scene CONTENTS only: this renders inside a <Canvas> supplied by the page that
 * composes it. It never creates a Canvas, never fetches the manifest itself
 * (pass `tree` or `manifest`), and never talks to the backend.
 *
 * ── Props ────────────────────────────────────────────────────────────────────
 *   tree            object   result of buildTree() — preferred
 *   manifest        object   raw manifest; used to build a tree when `tree` is absent
 *   t               number   explode parameter, 0..1 (damped internally)
 *   selectedId      string   manifest node id, or null
 *   isolatedId      string   manifest node id, or null — everything outside its
 *                            subtree (+ ancestors) is dimmed or hidden
 *   onSelect        fn(id)   called with a node id on click, null on empty-space click
 *   onHover         fn(id)   called with a node id on hover, null on leave
 *   showSkin        bool     mount the manifest's decorative skin, if any (default false)
 *   isolateMode     'dim'|'hide'   default 'dim'
 *   hiddenIds       Set<string>|null  explicitly hidden node ids; each hides its
 *                            own subtree outright (not dimmed, not pickable)
 *   showProvenance  bool     R5 marker, default true — turn off ONLY if the page
 *                            already renders the marker in its own DOM
 *   lights          bool     default true
 *   damping         number   damped-lerp lambda, default 10 /s
 *   scale           number   mm -> scene units, default 0.001 (vehicle ≈ 3.8 units)
 *   recenter        bool     default true — X/Z centred, ground plane at y = 0
 *   highlightId     string   externally driven hover glow (e.g. a hovered citation)
 *   partsApiRef     ref      filled with { centreOf, boxOf, isSettled } once the GLB
 *                            is bound: world-space centre/bbox of any part at its
 *                            CURRENT exploded position, for overlays and camera rigs
 *
 * ── Frames ───────────────────────────────────────────────────────────────────
 * The GLB root (ACE_EV_SYNTHETIC) carries a -90° X rotation, so its children live
 * in the CAD frame (mm, +X forward, +Y left, +Z up) while the scene above it is
 * Y-up. Explode vectors from the manifest are CAD-frame, so they are applied in
 * the local space of the glb_root's children; the walk below carries the
 * accumulated basis so this stays correct even if a future GLB nests deeper.
 *
 * ── Perf (Pi 4 / 4 GB) ───────────────────────────────────────────────────────
 * All scratch objects are module-level. useFrame does one damp() and, only while
 * the value is still moving, one position.set() per part — no allocation, no
 * setState, no material churn. Highlight/isolate are event-driven effects.
 */

import React, {
  Suspense, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import * as THREE from 'three';
import { useFrame, useLoader, useThree } from '@react-three/fiber';
import { Html, useGLTF } from '@react-three/drei';
// Same loader class drei's useGLTF uses, so both share one loader cache.
import { GLTFLoader } from 'three-stdlib';
import { sceneLoad } from './sceneLoadStatus.js';

import { buildTree } from './assemblyTree.js';
import { buildExplodeVectors, clamp01, damp } from './explodeMath.js';
import './ExplodedScene.css';

/* ------------------------------------------------------------------ scratch */
/* Module-level: never allocated inside useFrame. */
const _m3 = new THREE.Matrix3();
const _v3 = new THREE.Vector3();
const _box = new THREE.Box3();
const _size = new THREE.Vector3();
const _centre = new THREE.Vector3();
const _inv = new THREE.Matrix4();
const ZERO3 = Object.freeze([0, 0, 0]);
const _accent = new THREE.Color('#66B4C4'); // Varahi Blue 300, the console accent
// Scratch colour for diagnostic overlay fills — never allocate per mesh.
const _overlay = new THREE.Color();
const DEFAULT_RAYCAST = THREE.Mesh.prototype.raycast;

const BASE_URL = (typeof import.meta !== 'undefined' && import.meta.env
  && import.meta.env.BASE_URL) || '/';

export const DEFAULT_MODEL_PATH = 'cad/ace_ev_synthetic.glb';
export const DEFAULT_GLB_ROOT = 'ACE_EV_SYNTHETIC';
export const PROVENANCE_TEXT = 'Representative geometry — not OEM CAD';

const ACCENT = '#66B4C4';

/**
 * Pin a drei <Html fullscreen> overlay to the canvas rather than to the
 * anchor object's projected position: `fullscreen` offsets the wrapper by
 * -w/2,-h/2, so reporting the canvas centre makes it cover exactly the canvas.
 * Constant, so it also costs nothing per frame.
 */
const CANVAS_CENTRE = (_el, _camera, size) => [size.width / 2, size.height / 2];

/**
 * Fallback per-assembly base colours (index = A<n>) for manifests without an
 * `assemblies` array. Muted, so the accent reads as highlight.
 */
const ASSEMBLY_COLORS = {
  1: '#6b7690', // chassis frame — steel
  2: '#9a7bd0', // powertrain
  3: '#2f9e6e', // energy storage
  4: '#d08a3c', // HV electrical
  5: '#4f8fd6', // control electronics
  6: '#3fb0c4', // thermal
  7: '#8a8f99', // running gear
  8: '#b8b0a0', // cab body
  9: '#7d8b6a', // load body
};
const FALLBACK_COLOR = '#7a8296';

function assemblyColorFor(id, palette) {
  const m = /^[ASU](\d+)/.exec(String(id ?? ''));
  if (!m) return FALLBACK_COLOR;
  return palette?.get(Number(m[1])) || ASSEMBLY_COLORS[Number(m[1])] || FALLBACK_COLOR;
}

/** manifest.assemblies [{id:'A1', color}] -> Map(1 -> color). */
function paletteFromManifest(manifest) {
  const map = new Map();
  for (const a of manifest?.assemblies ?? []) {
    const m = /^A(\d+)$/.exec(String(a?.id ?? ''));
    if (m && a.color) map.set(Number(m[1]), a.color);
  }
  return map;
}

/* ------------------------------------------------------------------ helpers */

function joinUrl(path) {
  const p = String(path ?? '').replace(/^\/+/, '');
  return `${BASE_URL}${p}`;
}

/** Union bbox of every manifest node, expressed in the Y-up scene frame. */
function cadEnvelopeFromTree(tree) {
  let minx = Infinity; let miny = Infinity; let minz = Infinity;
  let maxx = -Infinity; let maxy = -Infinity; let maxz = -Infinity;
  for (const node of tree?.nodes ?? []) {
    const b = node?.bbox;
    if (!b || !Array.isArray(b.min) || !Array.isArray(b.max)) continue;
    minx = Math.min(minx, b.min[0]); maxx = Math.max(maxx, b.max[0]);
    miny = Math.min(miny, b.min[1]); maxy = Math.max(maxy, b.max[1]);
    minz = Math.min(minz, b.min[2]); maxz = Math.max(maxz, b.max[2]);
  }
  if (!Number.isFinite(minx)) return null;
  // worldFromCad(x, y, z) = (x, z, -y)
  return {
    min: [minx, minz, -maxy],
    max: [maxx, maxz, -miny],
  };
}

/** {id -> {centroid, bboxDiagonal}} for explodeMath, straight off the manifest. */
function centroidsFromTree(tree) {
  const out = new Map();
  for (const node of tree?.nodes ?? []) {
    const b = node?.bbox;
    let diagonal = 0;
    if (b && Array.isArray(b.min) && Array.isArray(b.max)) {
      const dx = b.max[0] - b.min[0];
      const dy = b.max[1] - b.min[1];
      const dz = b.max[2] - b.min[2];
      diagonal = Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
    out.set(node.id, { centroid: node.centre ?? node.centroid ?? [0, 0, 0], diagonal });
  }
  return out;
}

/**
 * Walk the CAD frame object, bind every object whose name is a manifest id, and
 * precompute the incremental explode offset each one must apply *in its own
 * parent's local space*.
 *
 *   applied(P) = cumulative(P) - cumulative(nearest bound ancestor of P)
 *
 * so the scene graph re-accumulates ancestors for us and the world result is
 * exactly `pos(t) = base + Σ ancestors + own`, whether the GLB nests or not.
 */
function bindParts(frameObject, tree, vectors, glbRoot = DEFAULT_GLB_ROOT) {
  const parts = [];
  const partById = new Map();
  const meshes = [];
  const glbNames = [];

  const identity = new THREE.Matrix4();

  const walk = (obj, parentFromFrame, ownerId) => {
    let nextOwner = ownerId;

    if (obj.name) glbNames.push(obj.name);

    if (obj.name && tree.byId.has(obj.name) && !partById.has(obj.name)) {
      const id = obj.name;
      const cum = vectors.get(id)?.cumulative ?? ZERO3;
      const pcum = (ownerId && vectors.get(ownerId)?.cumulative) || ZERO3;

      // delta in CAD-frame coords -> the object's parent's local basis
      _v3.set(cum[0] - pcum[0], cum[1] - pcum[1], cum[2] - pcum[2]);
      _m3.setFromMatrix4(parentFromFrame).invert();
      _v3.applyMatrix3(_m3);

      const part = {
        id,
        object: obj,
        node: tree.byId.get(id),
        base: obj.position.clone(),
        off: _v3.clone(),
        meshes: [],
      };
      obj.userData.partId = id;
      parts.push(part);
      partById.set(id, part);
      nextOwner = id;
    }

    if (obj.isMesh) {
      obj.userData.partId = nextOwner;
      meshes.push({ mesh: obj, ownerId: nextOwner });
      const owner = nextOwner ? partById.get(nextOwner) : null;
      if (owner) owner.meshes.push(obj);
    }

    if (obj.children.length) {
      const childFrame = new THREE.Matrix4().multiplyMatrices(parentFromFrame, obj.matrix);
      for (const child of obj.children) walk(child, childFrame, nextOwner);
    }
  };

  for (const child of frameObject.children) walk(child, identity, null);

  return { parts, partById, meshes, glbNames };
}

/* One warning per (tree, glb) pairing — never per frame. */
const warnedFor = new WeakSet();

function warnMismatch(tree, glbNames, partById, glbRoot = DEFAULT_GLB_ROOT) {
  if (!tree || warnedFor.has(tree)) return;
  warnedFor.add(tree);
  const named = new Set(glbNames);
  const missingInGlb = [];
  for (const id of tree.byId.keys()) if (!named.has(id)) missingInGlb.push(id);
  const missingInManifest = [];
  for (const name of named) {
    if (tree.byId.has(name) || name === glbRoot || partById.has(name)) continue;
    // three.js names a node's child primitive meshes "<parent>_1", "<parent>_2"…
    // The CadQuery export produces one such child per solid, which added 310
    // phantom "missing" entries for the synthetic assemblies and made this warning cry wolf.
    // They are already bound correctly (the mesh inherits its parent's ownerId),
    // so a name that is just a bound id plus a numeric suffix is not a mismatch.
    if (tree.byId.has(name.replace(/_\d+$/, ''))) continue;
    missingInManifest.push(name);
  }
  if (missingInGlb.length || missingInManifest.length) {
     
    console.warn(
      '[ExplodedScene] manifest/GLB node mismatch',
      { missingInGlb, missingInManifest },
    );
  }
  if (tree.diagnostics?.length) {
     
    console.warn('[ExplodedScene] assembly tree diagnostics', tree.diagnostics);
  }
}

/* ------------------------------------------------------------------ errors */

/**
 * <Suspense> catches suspension, not rejection. useGLTF is backed by
 * suspend-react, which caches a failed load and re-throws it on every
 * subsequent render — with no boundary that unmounts the whole React root, so
 * a single failed fetch of the 59 MB skin would blank the entire dashboard.
 *
 * This boundary keeps the failure local: the rest of the scene keeps working,
 * and `useGLTF.clear(url)` drops the cached rejection so a retry can succeed.
 */
class GltfErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {

    console.warn(`[ExplodedScene] failed to load "${this.props.url}"`, error);
    sceneLoad.failed(this.props.url);
  }

  componentDidUpdate(prevProps) {
    // A new url is a fresh attempt.
    if (prevProps.url !== this.props.url && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  componentWillUnmount() {
    // Drop the cached rejection so re-opening the view retries the fetch.
    if (this.state.failed && this.props.url) {
      try { useGLTF.clear(this.props.url); } catch { /* nothing to clear */ }
      sceneLoad.reset(this.props.url);
    }
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

/* ------------------------------------------------------------------ skin */

/**
 * Decorative body shell. NOT to scale — fitted to the CAD envelope per-axis.
 * MANDATORY: raycast = () => null on every mesh, otherwise its 1.1M vertices
 * swallow every pick and nothing in the assembly is clickable.
 */
/**
 * The shell faces the opposite way to the CAD assembly, so it must be
 * yawed 180° before the bbox fit or the vehicle reads back-to-front — cab over
 * the load deck.
 *
 * Measured, not guessed. Sampling the shell's silhouette in 20 slices along its
 * X axis gives a nose that ramps up over the first ~35% (max height -0.033 →
 * 0.617) and then a perfectly flat roof of constant width (0.616 / 0.745) for
 * the remaining 65% — a cab-plus-box-body profile with its FRONT at -X. The
 * CAD's front is at +X (cab 1350–2750 mm, nose 2900 mm), hence the flip.
 *
 * Applied to the clone itself rather than to the wrapping group, so the fit
 * below still measures the rotated bounding box and re-centres correctly. A
 * negative scale would have been the shorter fix but inverts face winding,
 * which breaks lighting on a single-sided material.
 */
const SKIN_YAW = Math.PI;

function GhostSkin({ url, envelope, opacity, yaw = SKIN_YAW }) {
  const { scene } = useGLTF(url);

  const object = useMemo(() => {
    const clone = scene.clone(true);
    clone.rotation.y = yaw;
    clone.updateMatrixWorld(true);
    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color('#9fd8ff'),
      emissive: new THREE.Color('#0b2c44'),
      emissiveIntensity: 0.6,
      roughness: 0.85,
      metalness: 0.0,
      transparent: true,
      opacity: clamp01(opacity ?? 0.1),
      depthWrite: false,
      side: THREE.FrontSide,
    });
    clone.traverse((o) => {
      o.raycast = () => null;          // <- the mandatory bit
      if (o.isMesh) {
        o.material = material;
        o.castShadow = false;
        o.receiveShadow = false;
        o.renderOrder = -1;
      }
    });
    clone.userData.ghostMaterial = material;
    return clone;
  }, [scene, opacity, yaw]);

  useEffect(() => () => object.userData.ghostMaterial?.dispose(), [object]);

  const fit = useMemo(() => {
    if (!envelope) return { position: [0, 0, 0], scale: [1, 1, 1] };
    _box.setFromObject(object);
    _box.getSize(_size);
    _box.getCenter(_centre);
    const sx = _size.x > 1e-6 ? (envelope.max[0] - envelope.min[0]) / _size.x : 1;
    const sy = _size.y > 1e-6 ? (envelope.max[1] - envelope.min[1]) / _size.y : 1;
    const sz = _size.z > 1e-6 ? (envelope.max[2] - envelope.min[2]) / _size.z : 1;
    return {
      scale: [sx, sy, sz],
      position: [
        (envelope.min[0] + envelope.max[0]) / 2 - _centre.x * sx,
        (envelope.min[1] + envelope.max[1]) / 2 - _centre.y * sy,
        (envelope.min[2] + envelope.max[2]) / 2 - _centre.z * sz,
      ],
    };
  }, [object, envelope]);

  return (
    <group position={fit.position} scale={fit.scale} raycast={() => null}>
      <primitive object={object} />
    </group>
  );
}

/* ------------------------------------------------------------------ CAD */

function CadAssembly({
  url, tree, t, selectedId, isolatedId, isolateMode, hiddenIds,
  onSelect, onHover, damping, overlayColors, glbRoot = DEFAULT_GLB_ROOT, palette = null,
  highlightId = null, partsApiRef = null,
}) {
  // useLoader rather than useGLTF only to receive byte progress for the loading
  // overlay; the cache key (loader class + url) is the same.
  const { scene } = useLoader(GLTFLoader, url, undefined,
    (e) => sceneLoad.progress(url, e.loaded, e.total));
  const invalidate = useThree((s) => s.invalidate);

  const [hoveredId, setHoveredId] = useState(null);
  const tRef = useRef(clamp01(t));
  const dirtyRef = useRef(true);

  // Own copy of the cached GLTF scene: never mutate the loader cache.
  const root = useMemo(() => {
    const clone = scene.clone(true);
    clone.updateMatrixWorld(true);
    return clone;
  }, [scene]);

  const vectors = useMemo(
    () => buildExplodeVectors(tree, centroidsFromTree(tree)),
    [tree],
  );

  const bound = useMemo(() => {
    const frame = root.getObjectByName(glbRoot) ?? root;
    const result = bindParts(frame, tree, vectors, glbRoot);
    warnMismatch(tree, result.glbNames, result.partById, glbRoot);
    // Part bbox in the part object's OWN local space, measured once. At runtime
    // it is carried by the object's matrixWorld, so it follows the explode.
    for (const part of result.parts) {
      _box.setFromObject(part.object);
      part.localBox = _box.isEmpty()
        ? null
        : _box.clone().applyMatrix4(_inv.copy(part.object.matrixWorld).invert());
    }
    return result;
  }, [root, tree, vectors, glbRoot]);

  // One material per mesh so emissive can be driven independently.
  useEffect(() => {
    const created = [];
    for (const { mesh, ownerId } of bound.meshes) {
      const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(assemblyColorFor(ownerId, palette)),
        roughness: 0.55,
        metalness: 0.25,
        emissive: new THREE.Color(ACCENT),
        emissiveIntensity: 0,
        // Created transparent ONCE and never flipped: `material.transparent`
        // feeds the `OPAQUE` shader define, and toggling it at runtime without
        // bumping material.version leaves `diffuseColor.a = 1.0` compiled in,
        // so the isolate-dim opacity would be silently ignored. Bumping the
        // version instead would recompile all 57 programs on every toggle.
        transparent: true,
        opacity: 1,
        depthWrite: true,
        // Converted FE decks are thin shell surfaces, not closed solids, so a
        // panel seen from its back face would simply vanish under FrontSide.
        // Costs fill rate; correctness wins.
        side: THREE.DoubleSide,
      });
      material.userData.baseColor = material.color.clone();
      mesh.material = material;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      created.push(material);
    }
    return () => { for (const m of created) m.dispose(); };
  }, [bound, palette]);

  // Loading overlay: "preparing" until a frame with these materials has been
  // drawn. The first draw compiles shaders and can block for seconds on large
  // models, so wait two animation frames after asking for a render.
  useEffect(() => {
    sceneLoad.preparing(url, bound.parts.length);
    invalidate();
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => sceneLoad.ready(url));
    });
    return () => { cancelAnimationFrame(first); cancelAnimationFrame(second); };
  }, [bound, url, invalidate]);

  /* ---- explode: the only per-frame work ---- */
  useFrame((_state, delta) => {
    const target = clamp01(t);
    const next = damp(tRef.current, target, damping, delta);
    if (next === tRef.current && !dirtyRef.current) return;
    tRef.current = next;
    dirtyRef.current = false;

    const parts = bound.parts;
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      p.object.position.set(
        p.base.x + p.off.x * next,
        p.base.y + p.off.y * next,
        p.base.z + p.off.z * next,
      );
    }
    if (next !== target) invalidate();
  });

  // A new binding must re-apply positions once even when t has not changed.
  useEffect(() => { dirtyRef.current = true; invalidate(); }, [bound, invalidate]);

  /* ---- parts API for world-space overlays (no React state, read in useFrame) ---- */
  const tTargetRef = useRef(clamp01(t));
  useEffect(() => { tTargetRef.current = clamp01(t); }, [t]);
  useEffect(() => {
    if (!partsApiRef) return undefined;
    const api = {
      centreOf(id, out) {
        const p = bound.partById.get(id);
        if (!p || !p.localBox) return false;
        p.object.updateWorldMatrix(true, false);
        p.localBox.getCenter(out).applyMatrix4(p.object.matrixWorld);
        return true;
      },
      boxOf(id, out) {
        const p = bound.partById.get(id);
        if (!p || !p.localBox) return false;
        p.object.updateWorldMatrix(true, false);
        out.copy(p.localBox).applyMatrix4(p.object.matrixWorld);
        return true;
      },
      isSettled: () => !dirtyRef.current && tRef.current === tTargetRef.current,
    };
    partsApiRef.current = api;
    return () => { if (partsApiRef.current === api) partsApiRef.current = null; };
  }, [bound, partsApiRef]);

  /* ---- selection / hover / isolate: event-driven, never per frame ---- */
  useEffect(() => {
    const inSubtree = (id) => {
      if (!id || !tree.byId.has(id)) return null;
      const set = new Set([id]);
      for (const d of tree.descendantsOf(id)) set.add(d);
      return set;
    };
    const selected = inSubtree(selectedId);
    const hovered = inSubtree(hoveredId) ?? inSubtree(highlightId);

    let keep = null;
    if (isolatedId && tree.byId.has(isolatedId)) {
      keep = inSubtree(isolatedId);
      for (const a of tree.ancestorsOf(isolatedId)) keep.add(a);
    }

    // Explicitly hidden subtrees (owned by the composing page).
    let hide = null;
    if (hiddenIds && hiddenIds.size) {
      hide = new Set();
      for (const id of hiddenIds) {
        if (!tree.byId.has(id)) continue;
        hide.add(id);
        for (const d of tree.descendantsOf(id)) hide.add(d);
      }
      if (hide.size === 0) hide = null;
    }

    for (const { mesh, ownerId } of bound.meshes) {
      const material = mesh.material;
      if (!material || !material.isMeshStandardMaterial) continue;

      if (hide && ownerId != null && hide.has(ownerId)) {
        mesh.visible = false;
        mesh.raycast = () => null;
        continue;
      }

      const isKept = keep === null || (ownerId != null && keep.has(ownerId));
      const isSelected = selected !== null && ownerId != null && selected.has(ownerId);
      const isHovered = hovered !== null && ownerId != null && hovered.has(ownerId);

      if (!isKept && isolateMode === 'hide') {
        mesh.visible = false;
        mesh.raycast = () => null;
        continue;
      }
      mesh.visible = true;

      if (!isKept) {
        material.opacity = 0.07;
        material.depthWrite = false;
        material.emissiveIntensity = 0;
        material.color.copy(material.userData.baseColor);
        mesh.raycast = () => null;
        continue;
      }

      material.opacity = 1;
      material.depthWrite = true;
      mesh.raycast = DEFAULT_RAYCAST;

      // Diagnostic overlay, when active, replaces the part's identity colour.
      // Reuses a scratch Color so the frame budget is unaffected — this effect
      // is event-driven, but the map can be large.
      const overlayHex = overlayColors ? overlayColors.get(ownerId) : null;
      const base = overlayHex
        ? _overlay.set(overlayHex)
        : material.userData.baseColor;

      if (isSelected) {
        material.emissiveIntensity = 0.55;
        material.color.copy(base).lerp(_accent, 0.35);
      } else if (isHovered) {
        material.emissiveIntensity = 0.22;
        material.color.copy(base);
      } else {
        material.emissiveIntensity = 0;
        material.color.copy(base);
      }
    }
    invalidate();
  }, [bound, tree, selectedId, hoveredId, highlightId, isolatedId, isolateMode, hiddenIds,
      overlayColors, invalidate]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!hoveredId) return undefined;
    const previous = document.body.style.cursor;
    document.body.style.cursor = 'pointer';
    return () => { document.body.style.cursor = previous; };
  }, [hoveredId]);

  /* ---- picking ---- */
  const idFromEvent = useCallback((event) => {
    let o = event.object;
    while (o) {
      if (o.userData && typeof o.userData.partId === 'string') return o.userData.partId;
      o = o.parent;
    }
    return null;
  }, []);

  const handleClick = useCallback((event) => {
    const id = idFromEvent(event);
    if (!id) return;
    event.stopPropagation();
    onSelect?.(id);
  }, [idFromEvent, onSelect]);

  const handleOver = useCallback((event) => {
    const id = idFromEvent(event);
    if (!id) return;
    event.stopPropagation();
    setHoveredId((prev) => (prev === id ? prev : id));
    onHover?.(id);
  }, [idFromEvent, onHover]);

  const handleOut = useCallback((event) => {
    event.stopPropagation();
    setHoveredId(null);
    onHover?.(null);
  }, [onHover]);

  const handleMissed = useCallback(() => { onSelect?.(null); }, [onSelect]);

  const label = hoveredId ?? highlightId ?? selectedId ?? null;
  const labelNode = label ? tree.byId.get(label) : null;

  return (
    <>
      <primitive
        object={root}
        onClick={handleClick}
        onPointerOver={handleOver}
        onPointerOut={handleOut}
        onPointerMissed={handleMissed}
      />
      {labelNode ? (
        <Html
          fullscreen
          calculatePosition={CANVAS_CENTRE}
          zIndexRange={[80, 0]}
          style={{ pointerEvents: 'none' }}
          wrapperClass="exploded-scene__html"
        >
          <div className="exploded-scene__overlay">
            <div className="exploded-scene__label">
              <span className="exploded-scene__label-name">{labelNode.name}</span>
              <span className="exploded-scene__label-id">{labelNode.id}</span>
            </div>
          </div>
        </Html>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ scene */

export default function ExplodedScene({
  tree: treeProp,
  manifest = null,
  t = 0,
  selectedId = null,
  isolatedId = null,
  hiddenIds = null,
  onSelect,
  onHover,
  showSkin = false,
  isolateMode = 'dim',
  showProvenance = true,
  lights = true,
  damping = 10,
  scale = 0.001,
  recenter = true,
  modelUrl,
  skinUrl,
  overlayColors = null,
  highlightId = null,
  partsApiRef = null,
}) {
  const tree = useMemo(
    () => treeProp ?? buildTree(manifest ?? []),
    [treeProp, manifest],
  );

  const url = modelUrl ?? joinUrl(manifest?.model ?? DEFAULT_MODEL_PATH);
  const glbRoot = manifest?.glb_root ?? DEFAULT_GLB_ROOT;
  const ghostUrl = skinUrl ?? (manifest?.skin?.model ? joinUrl(manifest.skin.model) : null);
  const palette = useMemo(() => paletteFromManifest(manifest), [manifest]);
  const ghostOpacity = manifest?.skin?.opacity ?? 0.1;

  const envelope = useMemo(() => cadEnvelopeFromTree(tree), [tree]);

  const offset = useMemo(() => {
    if (!recenter || !envelope) return [0, 0, 0];
    return [
      -(envelope.min[0] + envelope.max[0]) / 2,
      -envelope.min[1],
      -(envelope.min[2] + envelope.max[2]) / 2,
    ];
  }, [recenter, envelope]);

  const empty = tree.byId.size === 0;

  return (
    <group scale={scale}>
      {lights ? (
        <>
          <ambientLight intensity={0.55} />
          <hemisphereLight args={['#A6D3DD', '#0B1418', 0.5]} />
          <directionalLight position={[4000, 6000, 3000]} intensity={1.1} />
          <directionalLight position={[-4000, 2500, -3000]} intensity={0.4} color={ACCENT} />
        </>
      ) : null}

      <group position={offset}>
        {!empty ? (
          <GltfErrorBoundary url={url}>
            <Suspense fallback={null}>
              <CadAssembly
                url={url}
                tree={tree}
                t={t}
                selectedId={selectedId}
                isolatedId={isolatedId}
                isolateMode={isolateMode}
                hiddenIds={hiddenIds}
                onSelect={onSelect}
                onHover={onHover}
                damping={damping}
                overlayColors={overlayColors}
                glbRoot={glbRoot}
                palette={palette}
                highlightId={highlightId}
                partsApiRef={partsApiRef}
              />
            </Suspense>
          </GltfErrorBoundary>
        ) : null}

        {showSkin && ghostUrl ? (
          <GltfErrorBoundary url={ghostUrl}>
            <Suspense fallback={null}>
              <GhostSkin url={ghostUrl} envelope={envelope} opacity={ghostOpacity} />
            </Suspense>
          </GltfErrorBoundary>
        ) : null}
      </group>

      {showProvenance ? (
        <Html
          fullscreen
          calculatePosition={CANVAS_CENTRE}
          zIndexRange={[90, 0]}
          style={{ pointerEvents: 'none' }}
          wrapperClass="exploded-scene__html"
        >
          <div className="exploded-scene__overlay">
            <div className="exploded-scene__provenance" role="note">
              <span className="exploded-scene__provenance-dot" aria-hidden="true" />
              {PROVENANCE_TEXT}
            </div>
          </div>
        </Html>
      ) : null}
    </group>
  );
}

export { GhostSkin };

/* Only the small CAD file is preloaded — the 59 MB skin is loaded on demand. */
useGLTF.preload(joinUrl(DEFAULT_MODEL_PATH));
